import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  X,
  CheckSquare,
  CalendarRange,
  Layers,
  Trash2,
  Users,
  Bell,
  Link2,
  Paperclip,
  Plus,
  UserCog,
  Building2,
  ChevronDown,
} from "lucide-react";
import { api } from "../api";
import { Button } from "../ui";
import { useT } from "../i18n";
import { useToast } from "../ui/toast";
import { useModal } from "../ui/modal";
import { useGermanDateField, DE_DATE_HINT } from "../ui/germanDateField";
import { UserPicker, type Person } from "./UserPicker";
import {
  useDocAttachment,
  AttachInput,
  AttachmentChip,
  ATTACH_ACCEPT,
} from "./useDocAttachment";

interface PhaseOpt {
  id: string;
  name: string;
}

export interface TaskFormSeed {
  id: string;
  title: string;
  description: string | null;
  start_date: string | null;
  due_date: string | null;
  phase_id: string | null;
  status?: string | null;
}

interface TaskOut {
  id: string;
  project_id: string | null;
  phase_id: string | null;
  title: string;
  description: string | null;
  status: string;
  start_date: string | null;
  due_date: string | null;
  owner: string;
  created_at: string;
  updated_at: string;
}

/** Reminder-Intervall-Presets → Tage (null = kein wiederkehrendes Intervall). */
type ReminderKind =
  | "keine"
  | "einmalig"
  | "taeglich"
  | "woechentlich"
  | "zweiwoechentlich"
  | "monatlich"
  | "custom";

const REMINDER_INTERVAL: Record<ReminderKind, number | null> = {
  keine: null,
  einmalig: null,
  taeglich: 1,
  woechentlich: 7,
  zweiwoechentlich: 14,
  monatlich: 30,
  custom: 7,
};

/** Reihenfolge der Auswahl = Reihenfolge dieser Schlüssel. Die Texte kommen aus
 *  dem Katalog der aktiven Sprache (docs/i18n.md). */
const REMINDER_ORDER: ReminderKind[] = [
  "keine",
  "einmalig",
  "taeglich",
  "woechentlich",
  "zweiwoechentlich",
  "monatlich",
  "custom",
];

interface LinkDraft {
  url: string;
  label: string;
}

interface DepartmentOpt {
  department: string;
  member_count: number;
}

export default function TaskWizard({
  open,
  onClose,
  projectId,
  defaultPhaseId,
  defaultStatus,
  phases,
  task,
  onCreated,
  onUpdated,
  onDeleted,
  onAssigned,
}: {
  open: boolean;
  onClose: () => void;
  /** null = projektfreie Aufgabe (z.B. reiner HR-Reminder). */
  projectId: string | null;
  defaultPhaseId: string | null;
  /** Ziel-Status beim Anlegen (Kanban-Spalte). Default "open". */
  defaultStatus?: string;
  phases: PhaseOpt[];
  /** Wenn gesetzt: Edit-Mode (PATCH statt POST, Delete-Button sichtbar). */
  task?: TaskFormSeed | null;
  onCreated?: (t: TaskOut) => void;
  onUpdated?: (t: TaskOut) => void;
  onDeleted?: (id: string) => void;
  /** Fan-out an mehrere Mitarbeiter fertig — Aufrufer kann das Board neu laden. */
  onAssigned?: (tasks: TaskOut[]) => void;
}) {
  // Bewusst nur der Wizard-Abschnitt und nicht der ganze Katalog als `t`: die
  // Funktion `submit()` bindet `const t = await api.patch…` — ein Katalog
  // namens `t` wäre dort verdeckt.
  const w = useT().tasks.wizard;
  const ct = useT().common;
  const toast = useToast();
  const modal = useModal();
  const isEdit = !!task;

  const REMINDER_LABEL: Record<ReminderKind, string> = {
    keine: w.intervallKeine,
    einmalig: w.intervallEinmalig,
    taeglich: w.intervallTaeglich,
    woechentlich: w.intervallWoechentlich,
    zweiwoechentlich: w.intervallZweiwoechentlich,
    monatlich: w.intervallMonatlich,
    custom: w.intervallCustom,
  };

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [phaseId, setPhaseId] = useState<string | "">("");
  const [busy, setBusy] = useState(false);

  // Fremd-Zuweisung + Reminder + Anhänge (Migration 0057) — nur im Create-Mode.
  const [assignees, setAssignees] = useState<Person[]>([]);
  // Abteilungs-Ziele (Punkt 2): einzelne Abteilungen + „Alle Abteilungen".
  // Server expandiert zu Member-Emails (dedupe). Ziel-Chips visuell abgesetzt.
  const [departments, setDepartments] = useState<DepartmentOpt[]>([]);
  const [selectedDepts, setSelectedDepts] = useState<string[]>([]);
  const [allDepartments, setAllDepartments] = useState(false);
  const [deptPickerOpen, setDeptPickerOpen] = useState(false);
  const [delegatable, setDelegatable] = useState(true);
  // Default: wöchentliche Erinnerung (HR-Use-Case — die meisten Zuweisungen
  // sollen nudgen). reminderDate wird beim Öffnen auf das Fälligkeitsdatum
  // vorbelegt, damit der Reminder-Worker sofort einen Anker hat.
  const [reminderKind, setReminderKind] = useState<ReminderKind>("woechentlich");
  const [reminderDate, setReminderDate] = useState("");
  const [customDays, setCustomDays] = useState(7);
  const [links, setLinks] = useState<LinkDraft[]>([]);
  const [linkUrl, setLinkUrl] = useState("");
  const doc = useDocAttachment(projectId);
  // Drag&Drop-Highlight für den Anhang-Bereich (nur Create-Mode). dragDepth
  // zählt enter/leave, damit Child-Elemente den Drag-State nicht flackern lassen.
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);

  // Akzeptiert genau die Endungen aus ATTACH_ACCEPT (.pdf/.md/.markdown/.txt).
  const acceptsFile = (f: File) => {
    const name = f.name.toLowerCase();
    return ATTACH_ACCEPT.split(",").some((ext) => name.endsWith(ext.trim()));
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    if (isEdit) return; // Anhänge nur im Create-Mode
    const f = Array.from(e.dataTransfer.files).find(acceptsFile);
    if (f) doc.onFile(f);
    else if (e.dataTransfer.files.length > 0) {
      toast.err(w.dateiTypNichtUnterstuetzt, w.dateiTypErlaubt);
    }
  };

  useEffect(() => {
    if (!open) return;
    if (task) {
      setTitle(task.title);
      setDescription(task.description ?? "");
      setStartDate(task.start_date ?? "");
      setDueDate(task.due_date ?? "");
      setPhaseId(task.phase_id ?? "");
    } else {
      // Default Due-Date = today + 7 Tage (Backlog T1).
      const d = new Date();
      d.setDate(d.getDate() + 7);
      const dueDefault = d.toISOString().slice(0, 10);
      setTitle("");
      setDescription("");
      setStartDate("");
      setDueDate(dueDefault);
      setPhaseId(defaultPhaseId ?? "");
      setAssignees([]);
      setSelectedDepts([]);
      setAllDepartments(false);
      setDeptPickerOpen(false);
      setDelegatable(true);
      // Default „Wöchentlich" mit Erinnerungsdatum = Fälligkeit, damit
      // buildReminder() reminder_interval_days:7 + reminder_next_on liefert.
      setReminderKind("woechentlich");
      setReminderDate(dueDefault);
      setCustomDays(7);
      setLinks([]);
      setLinkUrl("");
      doc.clear();
    }
    setBusy(false);
    // doc.clear ist stabil (useCallback); bewusst nicht in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task, defaultPhaseId]);

  // Abteilungen laden (nur Create-Mode, sobald geöffnet).
  useEffect(() => {
    if (!open || isEdit) return;
    let cancelled = false;
    api
      .get<DepartmentOpt[]>("/users/departments")
      .then((list) => {
        if (!cancelled) setDepartments(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setDepartments([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isEdit]);

  const hasDeptTarget = allDepartments || selectedDepts.length > 0;
  // Datumsfelder deutsch (TT.MM.JJJJ). Die State-Werte bleiben ISO-Kalendertage
  // — `submit()` schickt sie unverändert als start_date/due_date/reminder_next_on.
  const startField = useGermanDateField(startDate, setStartDate);
  const dueField = useGermanDateField(dueDate, setDueDate);
  const reminderField = useGermanDateField(reminderDate, setReminderDate);
  // Ein unlesbares Datum sperrt das Anlegen: sonst reiste der zuletzt GÜLTIGE
  // Wert mit, während am Bildschirm etwas anderes steht (stiller Fehlwert).
  // Nur SICHTBARE Felder zählen mit: das Erinnerungsfeld gibt es nur im
  // Anlegen-Modus und nur bei aktivem Reminder. Zählte es immer mit, bliebe der
  // Knopf nach „Erinnerung: keine" gesperrt — ohne Feld, das die Meldung zeigt.
  const reminderShown = !isEdit && reminderKind !== "keine";
  const dateInvalid =
    startField.invalid || dueField.invalid || (reminderShown && reminderField.invalid);

  const canSubmit =
    title.trim().length > 0 && !busy && !doc.uploading && !dateInvalid;
  const isAssign = !isEdit && (assignees.length > 0 || hasDeptTarget);

  const toggleDept = (d: string) =>
    setSelectedDepts((ds) =>
      ds.includes(d) ? ds.filter((x) => x !== d) : [...ds, d]
    );

  /** Reminder-Payload aus den UI-Feldern (Create-Mode). */
  const buildReminder = () => {
    if (reminderKind === "keine") {
      return { reminder_interval_days: null, reminder_next_on: null };
    }
    const next = reminderDate || dueDate || new Date().toISOString().slice(0, 10);
    const interval =
      reminderKind === "custom"
        ? Math.max(1, customDays)
        : REMINDER_INTERVAL[reminderKind];
    return { reminder_interval_days: interval, reminder_next_on: next };
  };

  /** Links + Dokument an eine frisch erstellte Task hängen. */
  const applyAttachments = async (taskId: string) => {
    for (const l of links) {
      try {
        await api.post(`/tasks/${taskId}/attachments`, {
          kind: "link",
          url: l.url,
          label: l.label.trim() || null,
        });
      } catch {
        /* einzelner Link-Fehler kippt nicht die ganze Anlage */
      }
    }
    if (doc.hasDoc && doc.attachment?.doc) {
      try {
        await api.post(`/tasks/${taskId}/attachments`, {
          kind: "doc",
          document_id: doc.attachment.doc.id,
        });
      } catch {
        /* dito */
      }
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      if (isEdit && task) {
        const t = await api.patch<TaskOut>(`/tasks/${task.id}`, {
          title: title.trim(),
          description: description.trim() ? description.trim() : null,
          phase_id: phaseId ? phaseId : null,
          start_date: startDate ? startDate : null,
          due_date: dueDate ? dueDate : null,
        });
        onUpdated?.(t);
      } else if (isAssign) {
        const reminder = buildReminder();
        const created = await api.post<TaskOut[]>("/tasks/assign", {
          title: title.trim(),
          description: description.trim() || undefined,
          project_id: projectId || undefined,
          phase_id: phaseId || undefined,
          start_date: startDate || undefined,
          due_date: dueDate || undefined,
          assignees: assignees.map((a) => a.email),
          departments: allDepartments ? [] : selectedDepts,
          all_departments: allDepartments,
          delegatable,
          reminder_interval_days: reminder.reminder_interval_days,
          reminder_next_on: reminder.reminder_next_on,
        });
        for (const neu of created) await applyAttachments(neu.id);
        toast.ok(w.zugewiesenToast(created.length), title.trim());
        onAssigned?.(created);
      } else {
        const reminder = buildReminder();
        const t = await api.post<TaskOut>("/tasks", {
          title: title.trim(),
          description: description.trim() || undefined,
          project_id: projectId || undefined,
          phase_id: phaseId || undefined,
          status: defaultStatus || undefined,
          start_date: startDate || undefined,
          due_date: dueDate || undefined,
          reminder_interval_days: reminder.reminder_interval_days,
          reminder_next_on: reminder.reminder_next_on,
        });
        await applyAttachments(t.id);
        onCreated?.(t);
      }
      onClose();
    } catch (e) {
      toast.err(
        isEdit
          ? w.speichernFehlgeschlagen
          : isAssign
            ? w.zuweisenFehlgeschlagen
            : w.anlegenFehlgeschlagen,
        (e as Error).message
      );
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!isEdit || !task) return;
    const ok = await modal.confirm({
      title: w.loeschenFrage(task.title),
      confirmLabel: ct.loeschen,
      destructive: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/tasks/${task.id}`);
      onDeleted?.(task.id);
      onClose();
    } catch (e) {
      toast.err(w.loeschenFehlgeschlagen, (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addLink = () => {
    const u = linkUrl.trim();
    if (!u) return;
    setLinks((ls) => [...ls, { url: u, label: "" }]);
    setLinkUrl("");
  };

  const title_ = isEdit ? w.titelBearbeiten : isAssign ? w.titelZuweisen : w.titelNeu;
  const submitLabel = isEdit
    ? w.aktionSpeichern
    : isAssign
      ? w.aktionZuweisen
      : w.aktionAnlegen;

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 animate-fade-in" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-surface border border-border rounded-2xl shadow-2xl w-[560px] max-w-[92vw] max-h-[88vh] flex flex-col animate-modal-in">
          <div className="px-5 py-3 border-b border-border flex items-center gap-2">
            {isAssign ? (
              <Users className="w-4 h-4 text-brand-600" />
            ) : (
              <CheckSquare className="w-4 h-4 text-brand-600" />
            )}
            <Dialog.Title className="font-semibold text-sm flex-1">{title_}</Dialog.Title>
            <Dialog.Close asChild>
              <button className="p-1 text-text-3 hover:text-text">
                <X className="w-4 h-4" />
              </button>
            </Dialog.Close>
          </div>

          <div
            className="relative flex-1 overflow-y-auto px-5 py-4 space-y-4"
            onDragEnter={(e) => {
              if (isEdit) return;
              if (!Array.from(e.dataTransfer.types).includes("Files")) return;
              e.preventDefault();
              dragDepth.current += 1;
              setDragOver(true);
            }}
            onDragOver={(e) => {
              if (isEdit) return;
              if (Array.from(e.dataTransfer.types).includes("Files")) e.preventDefault();
            }}
            onDragLeave={() => {
              if (isEdit) return;
              dragDepth.current = Math.max(0, dragDepth.current - 1);
              if (dragDepth.current === 0) setDragOver(false);
            }}
            onDrop={onDrop}
          >
            {dragOver && !isEdit && (
              <div className="absolute inset-2 z-10 rounded-xl border-2 border-dashed border-brand-500 bg-brand-50/90 dark:bg-brand-900/40 flex items-center justify-center pointer-events-none">
                <div className="flex items-center gap-2 text-sm font-medium text-brand-700 dark:text-brand-200">
                  <Paperclip className="w-4 h-4" /> {w.dateiAblegen}
                </div>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-text mb-1.5 block">{w.feldTitel}</label>
              <input
                autoFocus
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey && canSubmit) {
                    e.preventDefault();
                    submit();
                  }
                }}
                placeholder={w.titelPlatzhalter}
                maxLength={300}
                className="w-full px-3 py-2 text-sm bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="text-xs font-medium text-text mb-1.5 block">
                {w.feldBeschreibung}{" "}
                <span className="text-text-3 font-normal">{w.optional}</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={w.beschreibungPlatzhalter}
                rows={3}
                className="w-full px-3 py-2 text-sm bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 resize-none"
              />
            </div>

            {/* Zuweisen an (nur Create-Mode). Leer = Aufgabe für mich selbst. */}
            {!isEdit && (
              <div>
                <label className="text-xs font-medium text-text mb-1.5 inline-flex items-center gap-1">
                  <Users className="w-3 h-3" /> {w.zuweisenAn}{" "}
                  <span className="text-text-3 font-normal">{w.zuweisenAnHinweis}</span>
                </label>
                <UserPicker selected={assignees} onChange={setAssignees} multiple />

                {/* Abteilungen zuweisen (Punkt 2). Server expandiert zu
                    Member-Emails; Ziel-Chips visuell abgesetzt (📁). */}
                {departments.length > 0 && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => setDeptPickerOpen((o) => !o)}
                      className="inline-flex items-center gap-1 text-xs text-text-2 hover:text-text"
                    >
                      <Building2 className="w-3 h-3" />
                      {w.ganzeAbteilung}
                      <ChevronDown
                        className={
                          "w-3 h-3 transition-transform " +
                          (deptPickerOpen ? "rotate-180" : "")
                        }
                      />
                    </button>
                    {(allDepartments || selectedDepts.length > 0) && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {allDepartments ? (
                          <span className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-warning-soft text-warning text-xs">
                            <Building2 className="w-3 h-3" /> {w.alleAbteilungen}
                            <button
                              type="button"
                              onClick={() => setAllDepartments(false)}
                              aria-label={w.alleAbteilungenEntfernen}
                              className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ) : (
                          selectedDepts.map((d) => (
                            <span
                              key={d}
                              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-warning-soft text-warning text-xs"
                            >
                              <Building2 className="w-3 h-3" /> {d}
                              <button
                                type="button"
                                onClick={() => toggleDept(d)}
                                aria-label={w.abteilungEntfernen(d)}
                                className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))
                        )}
                      </div>
                    )}
                    {deptPickerOpen && (
                      <div className="mt-1.5 max-h-44 overflow-y-auto rounded-md border border-border-strong bg-surface divide-y divide-border">
                        <button
                          type="button"
                          onClick={() => {
                            setAllDepartments((v) => !v);
                            if (!allDepartments) setSelectedDepts([]);
                          }}
                          className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-surface-2"
                        >
                          <span
                            className={
                              "w-4 h-4 rounded border flex items-center justify-center shrink-0 " +
                              (allDepartments
                                ? "bg-brand-500 border-brand-500 text-white"
                                : "border-border-strong")
                            }
                          >
                            {allDepartments && <span className="text-[10px]">✓</span>}
                          </span>
                          <span className="text-sm text-text font-medium">
                            {w.alleAbteilungen}
                          </span>
                        </button>
                        {departments.map((d) => {
                          const sel = !allDepartments && selectedDepts.includes(d.department);
                          return (
                            <button
                              key={d.department}
                              type="button"
                              disabled={allDepartments}
                              onClick={() => toggleDept(d.department)}
                              className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-surface-2 disabled:opacity-40"
                            >
                              <span
                                className={
                                  "w-4 h-4 rounded border flex items-center justify-center shrink-0 " +
                                  (sel
                                    ? "bg-brand-500 border-brand-500 text-white"
                                    : "border-border-strong")
                                }
                              >
                                {sel && <span className="text-[10px]">✓</span>}
                              </span>
                              <span className="flex-1 min-w-0 text-sm text-text truncate">
                                📁 {d.department}
                              </span>
                              <span className="text-[11px] text-text-3 shrink-0">
                                {d.member_count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {isAssign && (
                  <label className="mt-2 flex items-start gap-2 text-xs text-text-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={!delegatable}
                      onChange={(e) => setDelegatable(!e.target.checked)}
                    />
                    <span className="inline-flex items-center gap-1">
                      <UserCog className="w-3 h-3" />
                      <span>
                        <span className="font-medium">{w.persoenlich}</span>
                        {w.persoenlichVorNicht}
                        <span className="font-medium">{w.persoenlichNicht}</span>
                        {w.persoenlichNachNicht}
                        {!delegatable ? "" : w.persoenlichStandard}
                      </span>
                    </span>
                  </label>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-text mb-1.5 inline-flex items-center gap-1">
                  <CalendarRange className="w-3 h-3" /> {w.start}{" "}
                  <span className="text-text-3 font-normal">{w.optional}</span>
                </label>
                <input
                  {...startField.inputProps}
                  aria-label={w.startAria}
                  className={`w-full px-3 py-2 text-sm bg-surface border rounded-md focus:outline-none focus:ring-1 ${
                    startField.invalid
                      ? "border-danger focus:border-danger focus:ring-danger"
                      : "border-border-strong focus:border-brand-500 focus:ring-brand-500"
                  }`}
                />
                {startField.invalid && (
                  <p className="mt-1 text-xs text-danger">{DE_DATE_HINT}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-text mb-1.5 inline-flex items-center gap-1">
                  <CalendarRange className="w-3 h-3" /> {w.faellig}{" "}
                  <span className="text-text-3 font-normal">{w.optional}</span>
                </label>
                <input
                  {...dueField.inputProps}
                  aria-label={w.faelligAria}
                  className={`w-full px-3 py-2 text-sm bg-surface border rounded-md focus:outline-none focus:ring-1 ${
                    dueField.invalid
                      ? "border-danger focus:border-danger focus:ring-danger"
                      : "border-border-strong focus:border-brand-500 focus:ring-brand-500"
                  }`}
                />
                {dueField.invalid && (
                  <p className="mt-1 text-xs text-danger">{DE_DATE_HINT}</p>
                )}
              </div>
            </div>

            {/* Reminder (nur Create-Mode) */}
            {!isEdit && (
              <div>
                <label className="text-xs font-medium text-text mb-1.5 inline-flex items-center gap-1">
                  <Bell className="w-3 h-3" /> {w.erinnerung}{" "}
                  <span className="text-text-3 font-normal">{w.optional}</span>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={reminderKind}
                    onChange={(e) => {
                      const k = e.target.value as ReminderKind;
                      setReminderKind(k);
                      if (k !== "keine" && !reminderDate) {
                        setReminderDate(dueDate || new Date().toISOString().slice(0, 10));
                      }
                    }}
                    className="w-full px-3 py-2 text-sm bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                  >
                    {REMINDER_ORDER.map((k) => (
                      <option key={k} value={k}>
                        {REMINDER_LABEL[k]}
                      </option>
                    ))}
                  </select>
                  {reminderKind !== "keine" && (
                    <input
                      {...reminderField.inputProps}
                      title={w.erinnerungTitel}
                      aria-label={w.erinnerungAria}
                      className={`w-full px-3 py-2 text-sm bg-surface border rounded-md focus:outline-none focus:ring-1 ${
                        reminderField.invalid
                          ? "border-danger focus:border-danger focus:ring-danger"
                          : "border-border-strong focus:border-brand-500 focus:ring-brand-500"
                      }`}
                    />
                  )}
                </div>
                {reminderShown && reminderField.invalid && (
                  <p className="mt-1 text-xs text-danger">{DE_DATE_HINT}</p>
                )}
                {reminderKind === "custom" && (
                  <div className="mt-2 flex items-center gap-2 text-xs text-text-2">
                    <span>{w.alle}</span>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      value={customDays}
                      onChange={(e) => setCustomDays(parseInt(e.target.value, 10) || 1)}
                      className="w-20 px-2 py-1 bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500"
                    />
                    <span>{w.tageErinnern}</span>
                  </div>
                )}
              </div>
            )}

            {phases.length > 0 && (
              <div>
                <label className="text-xs font-medium text-text mb-1.5 inline-flex items-center gap-1">
                  <Layers className="w-3 h-3" /> {w.workstream}{" "}
                  <span className="text-text-3 font-normal">{w.optional}</span>
                </label>
                <select
                  value={phaseId}
                  onChange={(e) => setPhaseId(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                >
                  <option value="">{w.keinWorkstream}</option>
                  {phases.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Anhänge: Links + Dokument (nur Create-Mode) */}
            {!isEdit && (
              <div>
                <label className="text-xs font-medium text-text mb-1.5 inline-flex items-center gap-1">
                  <Paperclip className="w-3 h-3" /> {w.anhaenge}{" "}
                  <span className="text-text-3 font-normal">{w.optional}</span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Link2 className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-3 pointer-events-none" />
                    <input
                      type="text"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addLink();
                        }
                      }}
                      placeholder={w.linkPlatzhalter}
                      className="w-full pl-8 pr-3 py-2 text-sm bg-surface border border-border-strong rounded-md focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                    />
                  </div>
                  <Button variant="ghost" size="sm" onClick={addLink} icon={<Plus className="w-3.5 h-3.5" />}>
                    {w.link}
                  </Button>
                  <label className="cursor-pointer inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 px-2 py-2">
                    <Paperclip className="w-4 h-4" />
                    {doc.uploading ? w.laedt : w.datei}
                    <AttachInput api={doc} />
                  </label>
                </div>
                {(links.length > 0 || doc.attachment) && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {links.map((l, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full border border-border bg-surface-2 text-xs text-text-2 max-w-full"
                      >
                        <Link2 className="w-3 h-3 shrink-0" />
                        <span className="truncate max-w-[12rem]">{l.url}</span>
                        <button
                          type="button"
                          onClick={() => setLinks((ls) => ls.filter((_, j) => j !== i))}
                          aria-label={w.linkEntfernen}
                          className="rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-white/10"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                    {doc.attachment && (
                      <AttachmentChip attachment={doc.attachment} onRemove={doc.clear} />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-5 py-3 border-t border-border flex items-center justify-between gap-2">
            <div>
              {isEdit && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={remove}
                  disabled={busy}
                  icon={<Trash2 className="w-3.5 h-3.5" />}
                >
                  {ct.loeschen}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="md" onClick={onClose}>
                {ct.abbrechen}
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={submit}
                disabled={!canSubmit}
                loading={busy}
              >
                {submitLabel}
              </Button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
